import React, { VFC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EventBus } from 'src/common/EventBus';
import FileDropzone from 'src/common/FileDropzone';
import { useStepEditorContext } from 'src/store/step-editor';

import { Nullable } from 'src/types/nullable.type';
import { useFileUploader } from '../../use-file-uploader.hook';
import { StepEditorContentConfigureStageTypeEnum } from '../configure/stage-type.enum';
import { StepEditorContentFilePreview } from './preview';
import { StepEditorContentFileDropZone } from './drop-zone';
import { getFileInlineMetadata } from 'src/common/helpers/get-file-inline-metadata';

export const StepEditorContentFileUpload: VFC<{}> = () => {
  const inputRef = useRef<Nullable<HTMLElement>>(null);
  const [inlineFileMetaData, setInlineFileMetaData] = useState<Nullable<string>>(null);
  const { name, setName, setContentName, setCurrentConfigurationStage, currentConfigurationStage } =
    useStepEditorContext();

  const { uploadFile, resetUploader, uploadedFile, uploadInProgress, uploadPercentage } = useFileUploader();

  useEffect(() => resetUploader, [resetUploader]);

  const onFilesUpdate = useCallback(
    (files: File[]) => {
      if (files && files.length > 0) {
        setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
        const [updatedFile] = files;
        uploadFile(updatedFile);
      }
    },
    [uploadFile, setCurrentConfigurationStage]
  );

  const onUploadClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const onUploadCompleted = useCallback(
    async (file: File) => {
      if (!name) {
        setName(file.name.slice(0, file.name.lastIndexOf('.')));
      }
      setContentName(file.name);
      const inlineMetaData = await getFileInlineMetadata(file);
      setInlineFileMetaData(inlineMetaData);
    },
    [setContentName, name]
  );

  const isUploadCompleted = useMemo(
    () => uploadPercentage === 100 && !uploadInProgress,
    [uploadInProgress, uploadPercentage]
  );

  if (uploadedFile) {
    const onEditClicked = () => {
      EventBus.emit('editor:reset-content');
    };

    return (
      <>
        {currentConfigurationStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE && (
          <StepEditorContentFilePreview
            file={uploadedFile}
            fileMeta={inlineFileMetaData}
            onEditClick={onEditClicked}
            onUploadCompleted={onUploadCompleted}
            disableEditing={!isUploadCompleted}
            uploadInProgress={uploadInProgress}
            uploadPercentage={uploadPercentage}
          />
        )}
      </>
    );
  }

  return (
    <div className='flex flex-col space-y-6 h-[calc(100%-4rem)]'>
      <div className='text-bedrock-p text-bedrock-black'>
        Pick any file to upload and it will be displayed within this Step or shown as an attachment
      </div>
      <button type='button' className='btn-bedrock-primary' onClick={onUploadClick}>
        Upload
      </button>
      <div className='hidden'>
        <FileDropzone
          type='file'
          className='file-dropzone'
          multiple={false}
          onFilesUpdate={onFilesUpdate}
          resetOnSuccess={true}
          dropzoneClasses={null}
          inputRef={inputRef}
        />
      </div>
      <StepEditorContentFileDropZone onClick={onUploadClick} onFileDrop={onFilesUpdate} />
    </div>
  );
};
